import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { dayTone, holidayOn, nonWorkingKind, type NonWorkingKind } from '../pages/time/calendarDays'
import { addMonths, monthDates, parseIso, today } from '../pages/time/dates'
import {
  AWAY_STATUSES,
  leaveCode,
  leaveOn,
  leaveTone,
  LEAVE_TYPES,
  type LeaveApplication,
  type Person,
} from '../pages/time/timeData'
import { inputClass, PeriodNav } from './ui'

export function leaveName(type: string): string {
  const cut = type.indexOf(' - ')
  return cut === -1 ? type : type.slice(cut + 3)
}

export type DaySegment =
  | { kind: 'run'; key: string; app: LeaveApplication; span: number }
  | { kind: 'day'; key: string; date: string }

export function rowSegments(
  dates: string[],
  personId: string,
  approved: LeaveApplication[],
): DaySegment[] {
  const segments: DaySegment[] = []
  let i = 0
  while (i < dates.length) {
    const date = dates[i]!
    const app = leaveOn(approved, personId, date)
    if (!app) {
      segments.push({ kind: 'day', key: date, date })
      i += 1
      continue
    }
    let j = i + 1
    while (j < dates.length && leaveOn(approved, personId, dates[j]!)?.id === app.id) j += 1
    segments.push({ kind: 'run', key: `${app.id}:${date}`, app, span: j - i })
    i = j
  }
  return segments
}

export function runDayKinds(dates: string[], offset: number, span: number): (NonWorkingKind | null)[] {
  return dates.slice(offset, offset + span).map(nonWorkingKind)
}

export function awayOfType(applications: LeaveApplication[], type: string): LeaveApplication[] {
  return applications.filter((a) => AWAY_STATUSES.includes(a.status) && (!type || a.leave_type === type))
}

function overlaps(a: LeaveApplication, first: string, last: string): boolean {
  return a.start_date <= last && a.end_date >= first
}

/** The people with approved leave overlapping `dates`, narrowed by a name/team/position search. */
export function awayRows(people: Person[], approved: LeaveApplication[], dates: string[], search: string): Person[] {
  if (dates.length === 0) return []
  const first = dates[0]!
  const last = dates[dates.length - 1]!
  const away = new Set(approved.filter((a) => overlaps(a, first, last)).map((a) => a.employee_id))
  const q = search.trim().toLowerCase()
  return people.filter(
    (p) => away.has(p.id) && (!q || `${p.name} ${p.team} ${p.position}`.toLowerCase().includes(q)),
  )
}

export function legendTypes(approved: LeaveApplication[], dates: string[]): string[] {
  if (dates.length === 0) return []
  const first = dates[0]!
  const last = dates[dates.length - 1]!
  const used = new Set(approved.filter((a) => overlaps(a, first, last)).map((a) => a.leave_type))
  return LEAVE_TYPES.filter((option) => used.has(option))
}

export type LeaveLoad = 'loading' | 'error' | 'ready'

/** "Nobody is away" is only said once the leave list has actually loaded. */
export function leaveCalendarMessage(load: LeaveLoad, rowCount: number): 'loading' | 'error' | 'empty' | null {
  if (load !== 'ready') return load
  return rowCount === 0 ? 'empty' : null
}

function withOffsets(
  segments: DaySegment[],
): Array<{ segment: DaySegment; offset: number }> {
  let offset = 0
  return segments.map((segment) => {
    const at = offset
    offset += segment.kind === 'run' ? segment.span : 1
    return { segment, offset: at }
  })
}

function nonWorkingOverlayTone(kind: NonWorkingKind): string {
  if (kind === 'company') return 'bg-sky-500/25 dark:bg-sky-300/20'
  if (kind === 'holiday') return 'bg-slate-900/30 dark:bg-slate-50/25'
  return 'bg-slate-900/10 dark:bg-slate-50/10'
}

/** A leave run's cell: full type name if it measures narrower than the cell's own rendered width, the code otherwise. */
function RunLabel({
  full,
  abbr,
  tone,
  title,
  onClick,
}: {
  full: string
  abbr: string
  tone: string
  title: string
  onClick: () => void
}) {
  const btnRef = useRef<HTMLButtonElement>(null)
  const probeRef = useRef<HTMLSpanElement>(null)
  const [showFull, setShowFull] = useState(false)

  useLayoutEffect(() => {
    const btn = btnRef.current
    const probe = probeRef.current
    if (!btn || !probe || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const style = getComputedStyle(btn)
      const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
      setShowFull(probe.scrollWidth <= btn.clientWidth - padding)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(btn)
    return () => observer.disconnect()
  }, [full])

  return (
    <button
      type="button"
      ref={btnRef}
      onClick={onClick}
      title={title}
      className={`relative block h-6 w-full overflow-hidden whitespace-nowrap rounded px-1 text-left text-[10px] font-medium leading-6 ${tone}`}
    >
      <span ref={probeRef} className="invisible absolute left-0 top-0 whitespace-nowrap">
        {full}
      </span>
      {showFull ? full : abbr}
    </button>
  )
}

function RunCell({
  app,
  kinds,
  personName,
  onSelect,
}: {
  app: LeaveApplication
  kinds: (NonWorkingKind | null)[]
  personName: string
  onSelect: () => void
}) {
  const span = kinds.length
  return (
    <td
      colSpan={span}
      className="h-6 border-b border-slate-100 p-0 text-center dark:border-slate-700/60"
    >
      <div className="relative h-6 w-full">
        <RunLabel
          full={leaveName(app.leave_type)}
          abbr={leaveCode(app.leave_type)}
          tone={leaveTone(app.leave_type)}
          title={`${personName} · ${app.leave_type} · ${app.start_date}–${app.end_date}`}
          onClick={onSelect}
        />
        <div
          aria-hidden
          // `rounded` here must match `RunLabel`'s `rounded` above.
          className="pointer-events-none absolute inset-0 overflow-hidden rounded border border-slate-400/60 dark:border-slate-500/50"
        >
          {kinds.map(
            (kind, i) =>
              kind && (
                <span
                  key={i}
                  className={`absolute inset-y-0 ${nonWorkingOverlayTone(kind)}`}
                  style={{ left: `${(i / span) * 100}%`, width: `${(1 / span) * 100}%` }}
                />
              ),
          )}
        </div>
      </div>
    </td>
  )
}

/** Truncates instead of wrapping. */
function DayCell({ date }: { date: string }) {
  const holiday = holidayOn(date)
  return (
    <td
      className={`h-6 border-b border-slate-100 p-0 text-center dark:border-slate-700/60 ${dayTone(date)}`}
    >
      {holiday && (
        <span
          title={holiday.title}
          className="block h-6 overflow-hidden truncate px-0.5 text-[8px] leading-6 text-slate-600 dark:text-slate-300"
        >
          {holiday.title}
        </span>
      )}
    </td>
  )
}

export function LeaveCalendar({
  people,
  applications,
  month,
  onMonthChange,
  onSelect,
  load = 'ready',
  rowsVisible,
}: {
  people: Person[]
  applications: LeaveApplication[]
  month: string
  onMonthChange: (month: string) => void
  onSelect: (app: LeaveApplication) => void
  load?: LeaveLoad
  /** Opt-in: cap the grid at this many person rows (the box scrolls inside) instead of the page-height guess. */
  rowsVisible?: number
}) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [type, setType] = useState('')

  const dates = useMemo(() => monthDates(month), [month])
  const now = today()
  const monthIndex = parseIso(month).getMonth()

  const approved = useMemo(() => awayOfType(applications, type), [applications, type])
  const rows = useMemo(() => awayRows(people, approved, dates, search), [people, approved, dates, search])
  const legend = useMemo(() => legendTypes(approved, dates), [approved, dates])
  const message = leaveCalendarMessage(load, rows.length)
  const hasRows = rows.length > 0
  const scroller = useRef<HTMLDivElement>(null)

  // On a phone the grid is wider than the screen, and the days that matter start at today.
  useEffect(() => {
    const box = scroller.current
    if (!box) return
    const cell = box.querySelector<HTMLElement>('[data-today]')
    const name = box.querySelector<HTMLElement>('thead th')
    box.scrollLeft = cell && name ? cell.offsetLeft - name.offsetWidth : 0
  }, [month, hasRows])

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {t('leave.calendarTitle')}
          </h2>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('time.findPerson')}
            aria-label={t('time.findPerson')}
            className={`py-1 ${inputClass}`}
          />
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            aria-label={t('leave.type')}
            className={`py-1 ${inputClass}`}
          >
            <option value="">{t('leave.allTypes')}</option>
            {LEAVE_TYPES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <PeriodNav
          label={`${parseIso(month).getFullYear()} · ${t(`time.month.${monthIndex}`)}`}
          onPrev={() => onMonthChange(addMonths(month, -1))}
          onNext={() => onMonthChange(addMonths(month, 1))}
          onToday={() => onMonthChange(today())}
        />
      </div>

      {hasRows && (
        <div
          ref={scroller}
          className={`${rowsVisible ? 'overflow-auto' : 'list-viewport overflow-x-auto'} rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800`}
          style={rowsVisible ? { maxHeight: `calc(2.75rem + ${rowsVisible} * 2.25rem)` } : undefined}
        >
          <table
            className="w-full table-fixed border-collapse text-xs [--who-name:7rem] sm:[--who-name:11rem]"
            style={{ minWidth: `calc(var(--who-name) + ${dates.length * 2}rem)` }}
          >
            <colgroup>
              <col style={{ width: 'var(--who-name)' }} />
              {dates.map((date) => (
                <col key={date} />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-20">
              <tr>
                <th className="sticky left-0 z-30 border-b border-r border-slate-200 bg-slate-50 p-2 text-left font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-400">
                  {t('field.name')}
                </th>
                {dates.map((date) => {
                  const holiday = holidayOn(date)
                  return (
                    <th
                      key={date}
                      data-today={date === now ? '' : undefined}
                      title={holiday?.title}
                      className={`border-b border-slate-200 bg-slate-50 p-1 text-center font-medium dark:border-slate-700 dark:bg-slate-900 ${dayTone(date)} ${
                        date === now
                          ? 'text-blue-700 dark:text-blue-300'
                          : 'text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      <span className="block text-[11px] leading-tight">
                        {t(`time.dow.${parseIso(date).getDay()}`)}
                      </span>
                      <span className="block tabular-nums leading-tight">
                        {parseIso(date).getDate()}
                      </span>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((person) => (
                <tr key={person.id}>
                  <th
                    scope="row"
                    className="sticky left-0 z-10 truncate border-b border-r border-slate-100 bg-white p-2 text-left font-medium text-slate-800 dark:border-slate-700/60 dark:bg-slate-800 dark:text-slate-100"
                  >
                    {person.name}
                  </th>
                  {withOffsets(rowSegments(dates, person.id, approved)).map(({ segment, offset }) =>
                    segment.kind === 'run' ? (
                      <RunCell
                        key={segment.key}
                        app={segment.app}
                        kinds={runDayKinds(dates, offset, segment.span)}
                        personName={person.name}
                        onSelect={() => onSelect(segment.app)}
                      />
                    ) : (
                      <DayCell key={segment.key} date={segment.date} />
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {message && (
        <p
          role={message === 'error' ? 'alert' : undefined}
          className={`rounded-xl border border-dashed p-6 text-center text-sm ${
            message === 'error'
              ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200'
              : 'border-slate-300 text-slate-500 dark:border-slate-600 dark:text-slate-400'
          }`}
        >
          {message === 'loading'
            ? t('calendar.loading')
            : message === 'error'
              ? t('calendar.leaveLoadFailed')
              : t('leave.nobodyAway')}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
        {legend.map((option) => (
          <span key={option} className="flex items-center gap-1">
            <span className={`inline-block h-3 w-5 rounded ${leaveTone(option)}`} />
            {option}
          </span>
        ))}
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-5 rounded bg-slate-300/70 dark:bg-slate-600/50" />
          {t('timeadmin.nationalHoliday')}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-5 rounded bg-sky-100 dark:bg-sky-950/40" />
          {t('timeadmin.companyHoliday')}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-5 rounded bg-slate-100 dark:bg-slate-700/40" />
          {t('timeadmin.weekend')}
        </span>
      </div>
    </section>
  )
}
